import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Center, Image, Link, Spinner, Text, VStack } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { isIOSDevice } from '../../../../../utils/browser';
import { GenericAttachment } from '../../types/generic-attachment';

type EmployeeSpotlightAttachmentProps = {
	attachment: GenericAttachment;
	bonus: Bonus;
};

export const EmployeeSpotlightAttachment: FC<EmployeeSpotlightAttachmentProps> = ({ attachment, bonus }) => {
	const { track } = useAnalytics();
	const currentUser = useCurrentUser();
	const { t } = useTranslation('recognition', { keyPrefix: 'bonus_card.spotlight' });

	// Show loading state when spotlight is being generated
	// Check both generation_in_progress (job started) and pending status (job queued but not started)
	const isGenerating = attachment.generation_in_progress || attachment.status === 'pending' || attachment.status === 'processing';

	if (isGenerating) {
		return (
			<Box
				bg="bg.surface"
				borderRadius="lg"
				border="1px solid"
				borderColor="border.default"
				overflow="hidden"
				p={3}
				pb={4}
				data-testid="employee-spotlight-frame"
			>
				<Center py={8} data-testid="employee-spotlight-loading">
					<VStack spacing={3}>
						<Spinner size="lg" color="primary.fg" />
						<Text fontSize="sm" color="text.soft" textAlign="center">
							{t('generating')}
						</Text>
					</VStack>
				</Center>
			</Box>
		);
	}

	if (!attachment.url || !attachment.image_url) {
		return null;
	}

	const aspectRatio = attachment.width != null && attachment.height != null ? `${attachment.width} / ${attachment.height}` : undefined;
	const linkTarget = isIOSDevice() ? undefined : '_blank';

	const handleClick = () => {
		track(AnalyticsEvent.Recognition.SpotlightClicked, {
			spotlightId: attachment.id,
			userId: currentUser.id,
			parentId: bonus.id,
			parentType: bonus.parent_bonus_id ? 'Comment' : 'Bonus',
		});
	};

	return (
		<Link href={attachment.url} target={linkTarget} onClick={handleClick} data-testid="employee-spotlight-link" _hover={{ textDecoration: 'none' }}>
			<Box
				bg="bg.surface"
				borderRadius="lg"
				border="1px solid"
				borderColor="border.default"
				overflow="hidden"
				p={3}
				pb={4}
				data-testid="employee-spotlight-frame"
			>
				<Image src={attachment.image_url} width="100%" aspectRatio={aspectRatio} borderRadius="md" data-testid="employee-spotlight-image" />
				{attachment.title && (
					<Text mt={4} fontWeight="semibold" fontSize="md" color="text.default" data-testid="employee-spotlight-title">
						{attachment.title}
					</Text>
				)}
			</Box>
		</Link>
	);
};
